import { useState } from "react";
import { JsonExplanationResponse } from "../store/repsaApi";
import { explainJsonData } from "../store/modules/chat/chatActions";

interface UseDataExplanationOptions {
  onError?: (error: string) => void;
  onLimitReached?: (limitInfo: {
    dailyMessageCount: number;
    dailyLimit: number;
  }) => void;
}

interface DailyLimitInfo {
  dailyMessageCount: number;
  dailyLimit: number;
  isLimitReached: boolean;
}

export const useDataExplanation = (options?: UseDataExplanationOptions) => {
  const [isLoading, setIsLoading] = useState(false);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [dailyLimitInfo, setDailyLimitInfo] = useState<DailyLimitInfo | null>(
    null
  );

  const explainData = async (
    jsonData: any,
    analysisType: string,
    mode: string,
    timePeriod: string,
    selectedYearText: string,
    category?: string | undefined,
    variableName?: string | undefined,
    variableNames?: string | undefined,
    aggregationType?: string | undefined
  ) => {
    setIsLoading(true);
    setExplanation(null);
    setIsOpen(true);

    try {
      const result = await explainJsonData(
        jsonData,
        analysisType,
        mode,
        timePeriod,
        selectedYearText,
        category,
        variableName,
        variableNames,
        aggregationType
      );

      // Handle JsonExplanationResponse format
      if (result && typeof result === "object") {
        const response = result as JsonExplanationResponse;

        // Extract explanation text
        const explanationText =
          response.explanation || "No explanation provided";
        setExplanation(explanationText);

        // Handle daily limit information
        if (
          response.dailyMessageCount !== undefined &&
          response.dailyLimit !== undefined
        ) {
          const limitInfo = {
            dailyMessageCount: response.dailyMessageCount,
            dailyLimit: response.dailyLimit,
            isLimitReached: response.isLimitReached || false,
          };
          setDailyLimitInfo(limitInfo);

          // Notify about limit reached
          if (limitInfo.isLimitReached && options?.onLimitReached) {
            options.onLimitReached({
              dailyMessageCount: limitInfo.dailyMessageCount,
              dailyLimit: limitInfo.dailyLimit,
            });
          }
        }
      } else if (typeof result === "string") {
        // Fallback for string response
        setExplanation(result);
      } else {
        setExplanation(String(result));
      }
    } catch (error) {
      console.error("Data explanation error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to explain data";
      options?.onError?.(errorMessage);
      setExplanation("Failed to get explanation. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const closeExplanation = () => {
    setIsOpen(false);
    setExplanation(null);
    setDailyLimitInfo(null);
  };

  return {
    explainData,
    explanation,
    isLoading,
    isOpen,
    closeExplanation,
    dailyLimitInfo,
  };
};
